import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth } from '../firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut
} from 'firebase/auth';
import { setDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Loading from '../components/Loading';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const isMasterAccount = email === 'dan@flair.ac';
    await setDoc(doc(db, "users", userCredential.user.uid), {
      email,
      approved: isMasterAccount,
      role: isMasterAccount ? 'director' : null,
      createdAt: new Date().toISOString()
    });
    return userCredential;
  }

  async function login(email, password) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    // Check if this is the master account and ensure it's properly set up
    if (email === 'dan@flair.ac') {
      const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));
      if (!userDoc.exists() || !userDoc.data().approved || userDoc.data().role !== 'director') {
        await setDoc(doc(db, "users", userCredential.user.uid), {
          email: 'dan@flair.ac',
          approved: true,
          role: 'director',
          updatedAt: new Date().toISOString()
        }, { merge: true });
      }
    }
    return userCredential;
  }

  function logout() {
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      if (user) {
        // Fetch user data including role
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          // Ensure master account is always approved and has director role
          if (user.email === 'dan@flair.ac' && (!data.approved || data.role !== 'director')) {
            await setDoc(doc(db, "users", user.uid), {
              approved: true,
              role: 'director',
              updatedAt: new Date().toISOString()
            }, { merge: true });
            setUserData({ ...data, approved: true, role: 'director' });
          } else {
            setUserData(data);
          }
        }
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    signup,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <Loading /> : children}
    </AuthContext.Provider>
  );
} 